import React from 'react';
import { Wrench, Clock, Mail } from 'lucide-react';

interface MaintenancePageProps {
  message?: string;
  endTime?: string | null;
}

const MaintenancePage: React.FC<MaintenancePageProps> = ({ 
  message = 'We are currently performing scheduled maintenance. Please check back shortly.',
  endTime 
}) => {
  const formatEndTime = (time: string) => {
    try {
      const date = new Date(time);
      return date.toLocaleString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return time;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full text-center">
        {/* Animated Icon */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-primary-100 rounded-full animate-pulse">
            <Wrench className="w-12 h-12 text-primary-600" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Under Maintenance
        </h1>

        {/* Message */}
        <p className="text-lg text-gray-600 mb-6">
          {message}
        </p>

        {/* End Time */}
        {endTime && (
          <div className="flex items-center justify-center gap-2 text-primary-600 mb-8">
            <Clock className="w-5 h-5" />
            <span className="font-medium">
              Expected to be back: {formatEndTime(endTime)}
            </span>
          </div>
        )}

        {/* Progress Animation */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-8 overflow-hidden">
          <div className="bg-primary-500 h-2 rounded-full animate-maintenance-progress"></div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
          <h2 className="font-semibold text-gray-900 mb-3">Need Immediate Assistance?</h2>
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <Mail className="w-5 h-5 text-primary-500" />
            <a 
              href="mailto:info@futuremindsacademy.net" 
              className="text-primary-600 hover:underline"
            >
              info@futuremindsacademy.net
            </a>
          </div>
        </div>

        {/* Logo/Branding */}
        <div className="mt-8">
          <p className="text-sm text-gray-500">
            Future Minds Academy
          </p>
        </div>
      </div>

      {/* Add custom animation styles */}
      <style>{`
        @keyframes maintenance-progress {
          0% { width: 0%; margin-left: 0; }
          50% { width: 50%; margin-left: 25%; }
          100% { width: 0%; margin-left: 100%; }
        }
        .animate-maintenance-progress {
          animation: maintenance-progress 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default MaintenancePage;
